import { getReportMetricKind } from '../shared/report-table-styles';
import ReportLead, { useReportLoading } from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { AppVarsContext } from '../../context/AppVarsContext';
import { getReportTabsSx } from '../shared/reportSurfaceStyles';
import { createReportSurfaceTheme, getReportSurfaceSx, reportColors, styleReportChart } from '../shared/report-design';
import PerformanceJourneyOverview from '../shared/PerformanceJourneyOverview';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(Number(value || 0));
const formatPercent = (value, digits = 1) => `${Number(value || 0).toFixed(digits)}%`;

const sumBy = (rows, key) => (rows || []).reduce((total, row) => total + Number(row?.[key] || 0), 0);

const getRangeDayCount = (range) => {
    const start = new Date(`${range?.start || ''}T00:00:00`).getTime();
    const end = new Date(`${range?.end || ''}T00:00:00`).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
    return Math.max(1, Math.round((end - start) / 86400000) + 1);
};

const getTrendMomentum = (trend = []) => {
    if (trend.length < 2) return null;
    const split = Math.ceil(trend.length / 2);
    const first = trend.slice(0, split);
    const second = trend.slice(split);
    if (!second.length) return null;
    const firstDaily = sumBy(first, 'visits') / first.length;
    const secondDaily = sumBy(second, 'visits') / second.length;
    if (!firstDaily) return secondDaily ? 100 : 0;
    return ((secondDaily - firstDaily) / firstDaily) * 100;
};

export const buildMatomoDecisionContext = (report) => {
    const channels = report?.traffic?.channels || [];
    const pages = report?.content?.topPages || [];
    const devices = report?.audience?.devices || [];
    const topChannel = channels[0];
    const topPage = pages[0];
    const topDevice = devices[0];
    const channelTotal = sumBy(channels, 'visits');
    const deviceTotal = sumBy(devices, 'visits');
    const momentum = getTrendMomentum(report?.trend || []);

    return [
        {
            label: 'Traffic momentum',
            value: momentum == null ? 'Not enough data' : `${momentum > 0 ? '+' : ''}${momentum.toFixed(1)}%`,
            helper: momentum == null ? 'Choose a range with at least two days.' : 'Daily visit pace: second half vs first half.',
        },
        {
            label: 'Leading channel',
            value: topChannel?.label || 'No channel data',
            helper: topChannel ? `${formatPercent(channelTotal ? (topChannel.visits / channelTotal) * 100 : 0, 0)} of the reported channel mix · ${formatNumber(topChannel.visits)} visits` : 'No acquisition source was reported.',
        },
        {
            label: 'Top content',
            value: topPage?.label || 'No page data',
            helper: topPage ? `${formatNumber(topPage.pageviews)} pageviews · ${formatDuration(topPage.avgTimeOnPage)} average time` : 'No page activity was reported.',
        },
        {
            label: 'Primary device',
            value: topDevice?.label || 'No device data',
            helper: topDevice ? `${formatPercent(deviceTotal ? (topDevice.visits / deviceTotal) * 100 : 0, 0)} of device visits · ${formatNumber(topDevice.visits)} visits` : 'No device mix was reported.',
        },
    ];
};

const formatDuration = (value) => {
    const total = Number(value || 0);
    if (!Number.isFinite(total) || total <= 0) return '0s';
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = Math.round(total % 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const parseSettings = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        console.error('Matomo report settings parse failed', error);
        return {};
    }
};

const EmptyState = ({ message }) => (
    <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
);

const TabPanel = ({ value, index, children }) => {
    if (value !== index) return null;
    return <Box sx={{ minWidth: 0 }} role="tabpanel" id={`matomo-panel-${index}`} aria-labelledby={`matomo-tab-${index}`}>{children}</Box>;
};

const SimpleTable = ({ rows, columns, emptyMessage }) => {
    if (!rows.length) {
        return <EmptyState message={emptyMessage} />;
    }

    return (
        <Box role="region" aria-label="Scrollable report table" tabIndex={0} sx={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}>
            <Table size="small" sx={{ minWidth: 620, '& th, & td': { px: 2, py: 1.25 }, '& td:first-of-type': { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 160 } }}>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}>
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.label || row.date || 'row'}-${index}`}>
                            {columns.map((column) => (
                                <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}>
                                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default function MatomoAnalyticsReport(props) {
    const cid = sessionStorage.getItem('rid') || '';
    const publicReportSlug = sessionStorage.getItem('reportSlug') || '';
    const { appVars, appLoading } = useContext(AppVarsContext);
    const profile = props.reportProfile;
    const [range, setRange] = useState(() => ({ sdate: sessionStorage.getItem('sDate'), edate: sessionStorage.getItem('eDate') }));
    const baseTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(baseTheme), [baseTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    useReportLoading(loading, props.setLoading);
    const [error, setError] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const requestId = useRef(0);

    const settings = useMemo(() => parseSettings(profile?.settings), [profile?.settings]);
    const matomoConfig = settings?.ad_sets?.matomo || {};
    const hasMatomoConfigured = useMemo(() => {
        const siteId = String(matomoConfig?.id || profile?.beaconId || '').trim();
        return Boolean(siteId && siteId !== '0');
    }, [matomoConfig?.id, profile?.beaconId]);

    const chartColors = reportColors.categories;

    const chartBaseOptions = useMemo(() => ({
        chart: {
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: theme.typography.fontFamily,
        },
        dataLabels: { enabled: false },
        grid: { borderColor: theme.palette.divider },
        colors: chartColors,
        tooltip: { theme: theme.palette.mode },
        legend: { position: 'bottom' },
    }), [chartColors, theme]);

    useEffect(() => {
        const updateRange = (event) => setRange({
            sdate: event.detail?.sDate || sessionStorage.getItem('sDate'),
            edate: event.detail?.eDate || sessionStorage.getItem('eDate'),
        });
        window.addEventListener('dc', updateRange);
        return () => window.removeEventListener('dc', updateRange);
    }, []);

    const fetchReport = useCallback(async () => {
        if (!appVars?.api_url || !cid || !hasMatomoConfigured) return;

        const activeRequest = ++requestId.current;
        try {
            setLoading(true);
            setReport(null);
            setError('');

            const response = await fetch(`${appVars.api_url}/matomo/report`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...(publicReportSlug ? { 'X-Report-Slug': publicReportSlug } : {}) },
                body: JSON.stringify({
                    cid,
                    sdate: range.sdate,
                    edate: range.edate,
                }),
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload?.error?.message || payload?.error || 'Failed to load Matomo analytics.');
            }
            if (requestId.current === activeRequest) setReport(payload);
        } catch (requestError) {
            if (requestId.current !== activeRequest) return;
            setReport(null);
            setError(requestError.message || 'Failed to load Matomo analytics.');
        } finally {
            if (requestId.current === activeRequest) setLoading(false);
        }
    }, [appVars?.api_url, cid, hasMatomoConfigured, publicReportSlug, range]);

    useEffect(() => {
        if (!profile || appLoading || !appVars?.api_url || !cid) return;
        if (!hasMatomoConfigured) {
            setReport(null);
            setError('Matomo is not configured for this client. Connect and save Matomo before opening this report.');
            return;
        }
        fetchReport();
        return () => { requestId.current += 1; };
    }, [appLoading, appVars?.api_url, cid, fetchReport, hasMatomoConfigured, profile]);

    const trendCategories = useMemo(
        () => (report?.trend || []).map((row) => row.date),
        [report?.trend]
    );
    const trendSeries = useMemo(() => ([
        { name: 'Visits', data: (report?.trend || []).map((row) => Number(row.visits || 0)) },
        { name: 'Unique Visitors', data: (report?.trend || []).map((row) => Number(row.uniqueVisitors || 0)) },
    ]), [report?.trend]);

    const channelRows = report?.traffic?.channels || [];
    const topWebsiteRows = report?.traffic?.websites || [];
    const searchRows = report?.traffic?.searchEngines || [];
    const campaignRows = report?.traffic?.campaigns || [];
    const pageRows = report?.content?.topPages || [];
    const countryRows = report?.audience?.countries || [];
    const deviceRows = report?.audience?.devices || [];
    const browserRows = report?.audience?.browsers || [];
    const decisionContext = useMemo(() => buildMatomoDecisionContext(report), [report]);
    const dayCount = getRangeDayCount(report?.range);
    const averageDailyVisits = dayCount ? Number(report?.summary?.visits || 0) / dayCount : 0;
    const revenuePerVisit = Number(report?.summary?.visits || 0)
        ? Number(report?.summary?.revenue || 0) / Number(report.summary.visits)
        : 0;

    const horizontalBarOptions = useCallback((categories) => styleReportChart(theme, {
        ...chartBaseOptions,
        chart: { ...chartBaseOptions.chart, type: 'bar' },
        colors: [reportColors.revenue],
        legend: { show: false },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '65%' } },
        xaxis: { categories, labels: { style: { colors: theme.palette.text.secondary } } },
        yaxis: { labels: { style: { colors: theme.palette.text.secondary } } },
    }), [chartBaseOptions, theme]);

    const lineOptions = useMemo(() => styleReportChart(theme, {
        ...chartBaseOptions,
        chart: { ...chartBaseOptions.chart, type: 'line' },
        stroke: { curve: 'straight', width: 3 },
        xaxis: { categories: trendCategories, labels: { style: { colors: theme.palette.text.secondary } } },
        yaxis: { labels: { style: { colors: theme.palette.text.secondary } } },
    }), [chartBaseOptions, theme, trendCategories]);

    const donutOptions = useCallback((labels) => styleReportChart(theme, {
        ...chartBaseOptions,
        chart: { ...chartBaseOptions.chart, type: 'donut' },
        labels,
        plotOptions: { pie: { donut: { size: '68%' } } },
    }), [chartBaseOptions, theme]);

    const pageColumns = [
        { key: 'label', label: 'Page' },
        { key: 'pageviews', label: 'Pageviews', align: 'right', render: (value) => formatNumber(value) },
        { key: 'entrances', label: 'Entrances', align: 'right', render: (value) => formatNumber(value) },
        { key: 'exits', label: 'Exits', align: 'right', render: (value) => formatNumber(value) },
        { key: 'bounceRate', label: 'Bounce Rate', align: 'right', render: (value) => formatPercent(value) },
        { key: 'avgTimeOnPage', label: 'Avg Time', align: 'right', render: (value) => formatDuration(value) },
    ];

    const trafficColumns = [
        { key: 'label', label: 'Source' },
        { key: 'visits', label: 'Visits', align: 'right', render: (value) => formatNumber(value) },
        { key: 'actions', label: 'Actions', align: 'right', render: (value) => formatNumber(value) },
        { key: 'conversions', label: 'Conversions', align: 'right', render: (value) => formatNumber(value) },
        { key: 'bounceRate', label: 'Bounce Rate', align: 'right', render: (value) => formatPercent(value) },
        { key: 'avgTimeOnSite', label: 'Avg Time', align: 'right', render: (value) => formatDuration(value) },
    ];

    const geoColumns = [
        { key: 'label', label: 'Country' },
        { key: 'visits', label: 'Visits', align: 'right', render: (value) => formatNumber(value) },
        { key: 'uniqueVisitors', label: 'Visitors', align: 'right', render: (value) => formatNumber(value) },
        { key: 'actions', label: 'Actions', align: 'right', render: (value) => formatNumber(value) },
    ];

    return (
        <ThemeProvider theme={theme}><Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2, p: { xs: 1, sm: 2, lg: 3 }, minWidth: 0, bgcolor: 'background.default',
                '& .MuiCard-root': getReportSurfaceSx(theme),
                '& .MuiCardHeader-title': { fontSize: '1rem', fontWeight: 700 },
                '& .MuiCardHeader-subheader': { fontSize: '0.875rem', mt: 0.5 },
                '& .MuiTableCell-head': { bgcolor: 'background.default', fontWeight: 700, whiteSpace: 'nowrap' },
                '& .MuiTableRow-root:nth-of-type(even)': { bgcolor: 'background.default' },
                '@media print': { p: 0 } }}>
                <Card sx={{ ...getReportSurfaceSx(theme), p: 1 }}>
                    <Tabs
                        value={tabIndex}
                        onChange={(_, next) => setTabIndex(next)}
                        variant={isMobile ? 'scrollable' : 'standard'}
                        scrollButtons={isMobile ? 'auto' : false}
                        sx={getReportTabsSx(theme)}
                        aria-label="Matomo report sections"
                    >
                        <Tab label="Traffic" id="matomo-tab-0" aria-controls="matomo-panel-0" />
                        <Tab label="Content" id="matomo-tab-1" aria-controls="matomo-panel-1" />
                        <Tab label="Audience" id="matomo-tab-2" aria-controls="matomo-panel-2" />
                    </Tabs>
                </Card>
                <Card><CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <ReportLead eyebrow="Matomo" title="Matomo Analytics" description="See how visitors find your site, explore its content, and convert." badge={report?.site?.name} />
                </CardContent></Card>

                {error ? <Alert severity="warning" action={hasMatomoConfigured ? <Button color="inherit" onClick={fetchReport}>Try again</Button> : undefined}>{error}</Alert> : null}



                {loading && !report ? (
                    <PerformanceJourneyOverview
                    variant="plain"
                    stageAccents={{ Acquisition: reportColors.revenue, Engagement: reportColors.conversions, Outcome: reportColors.categories[3] }}
                        eyebrow="Matomo performance"
                        title="From visits to measurable outcomes"
                        description="Loading website demand and outcome data while keeping the report layout stable."
                        metrics={[
                            { key: 'visits', stage: 'Acquisition', label: 'Visits', detail: 'Average per day' },
                            { key: 'visitors', stage: 'Acquisition', label: 'Unique visitors', detail: 'Distinct visitors' },
                            { key: 'actions-per-visit', stage: 'Engagement', label: 'Actions / visit', detail: 'Tracked actions' },
                            { key: 'bounce-rate', stage: 'Engagement', label: 'Bounce rate', detail: 'Average visit' },
                            { key: 'conversions', stage: 'Outcome', label: 'Conversions', detail: 'Visits converted' },
                            { key: 'revenue', stage: 'Outcome', label: 'Revenue', detail: 'Revenue per visit' },
                        ]}
                        supportMetrics={[
                            { label: 'Tracked actions' },
                            { label: 'Average visit' },
                            { label: 'Revenue / visit' },
                            { label: 'Daily visits' },
                        ]}
                        stageDescriptions={{
                            Acquisition: 'How much qualified website demand arrived',
                            Engagement: 'How deeply visitors explored the site',
                            Outcome: 'What that activity produced',
                        }}
                        highlights={Array.from({ length: 4 }, (_, index) => ({
                            key: `matomo-loading-highlight-${index}`,
                            eyebrow: ['Traffic volume', 'Engagement quality', 'Conversion signal', 'What stands out'][index],
                            title: 'Loading',
                            detail: 'Loading report detail',
                        }))}
                        loading
                    />
                ) : null}

                {report ? <>

                <PerformanceJourneyOverview
                    variant="plain"
                    stageAccents={{ Acquisition: reportColors.revenue, Engagement: reportColors.conversions, Outcome: reportColors.categories[3] }}
                    eyebrow="Matomo performance"
                    title="From visits to measurable outcomes"
                    description="Follow website demand from acquired visitors through engagement, conversion activity, and attributed revenue for the selected range."
                    metrics={[
                        { key: 'visits', stage: 'Acquisition', label: 'Visits', formatted: formatNumber(report?.summary?.visits), detail: `${formatNumber(averageDailyVisits)} average per day` },
                        { key: 'visitors', stage: 'Acquisition', label: 'Unique visitors', formatted: formatNumber(report?.summary?.uniqueVisitors), detail: 'Distinct visitors' },
                        { key: 'actions-per-visit', stage: 'Engagement', label: 'Actions / visit', formatted: Number(report?.summary?.actionsPerVisit || 0).toFixed(2), detail: `${formatNumber(report?.summary?.actions)} tracked actions` },
                        { key: 'bounce-rate', stage: 'Engagement', label: 'Bounce rate', formatted: formatPercent(report?.summary?.bounceRate), detail: `${formatDuration(report?.summary?.avgVisitDuration)} average visit` },
                        { key: 'conversions', stage: 'Outcome', label: 'Conversions', formatted: formatNumber(report?.summary?.conversions), detail: `${formatPercent(report?.summary?.conversionRate)} of visits converted` },
                        { key: 'revenue', stage: 'Outcome', label: 'Revenue', formatted: formatCurrency(report?.summary?.revenue), detail: `${formatCurrency(revenuePerVisit)} per visit` },
                    ]}
                    supportMetrics={[
                        { label: 'Tracked actions', formatted: formatNumber(report?.summary?.actions) },
                        { label: 'Average visit', formatted: formatDuration(report?.summary?.avgVisitDuration) },
                        { label: 'Revenue / visit', formatted: formatCurrency(revenuePerVisit) },
                        { label: 'Daily visits', formatted: formatNumber(averageDailyVisits) },
                    ]}
                    stageDescriptions={{
                        Acquisition: 'How much qualified website demand arrived',
                        Engagement: 'How deeply visitors explored the site',
                        Outcome: 'What that activity produced',
                    }}
                    badge={report?.site?.name || 'Matomo'}
                    highlights={decisionContext.map((signal, index) => ({
                        eyebrow: signal.label,
                        title: signal.value,
                        detail: signal.helper,
                        tone: index === 0 ? 'soft' : 'default',
                        accent: reportColors.categories[index],
                    }))}
                />

                <TabPanel value={tabIndex} index={0}>
                    <Stack spacing={2}>
                        <Card sx={getReportSurfaceSx(theme)}>
                            <CardHeader
                                title="Traffic Trend"
                                subheader="Daily visits and unique visitors reveal traffic shape without mixing in higher-scale action counts."
                            />
                            <CardContent>
                                {trendCategories.length ? (
                                    <ReactApexChart
                                        type="line"
                                        height={340}
                                        options={lineOptions}
                                        series={trendSeries}
                                    />
                                ) : (
                                    <EmptyState message="Matomo did not return daily trend data for this range." />
                                )}
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.15fr) minmax(0, 0.85fr)' }, gap: 2 }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader
                                    title="Channel Mix"
                                    subheader="Where site traffic came from at the highest level."
                                />
                                <CardContent>
                                    {channelRows.length ? (
                                        <ReactApexChart
                                            type="bar"
                                            height={320}
                                            options={horizontalBarOptions(channelRows.map((row) => row.label))}
                                            series={[{ name: 'Visits', data: channelRows.map((row) => row.visits) }]}
                                        />
                                    ) : (
                                        <EmptyState message="No channel rows were returned by Matomo." />
                                    )}
                                </CardContent>
                            </Card>

                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader
                                    title="Channel Detail"
                                    subheader="Visits, actions, bounce rate, and time on site by channel."
                                />
                                <CardContent>
                                    <SimpleTable
                                        rows={channelRows}
                                        columns={trafficColumns}
                                        emptyMessage="No channel detail is available for this range."
                                    />
                                </CardContent>
                            </Card>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Referring Websites" subheader="Top websites that sent visitors." />
                                <CardContent>
                                    <SimpleTable rows={topWebsiteRows} columns={trafficColumns} emptyMessage="No referring websites were returned." />
                                </CardContent>
                            </Card>

                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Search Engines" subheader="Search-driven traffic to the site." />
                                <CardContent>
                                    <SimpleTable rows={searchRows} columns={trafficColumns} emptyMessage="No search engine traffic was returned." />
                                </CardContent>
                            </Card>

                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Campaigns" subheader="Campaign attribution tracked in Matomo." />
                                <CardContent>
                                    <SimpleTable rows={campaignRows} columns={trafficColumns} emptyMessage="No campaign rows were returned." />
                                </CardContent>
                            </Card>
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={tabIndex} index={1}>
                    <Card sx={getReportSurfaceSx(theme)}>
                        <CardHeader
                            title="Top Pages"
                            subheader="Pages attracting the most traffic and where visitors entered or exited."
                        />
                        <CardContent>
                            <SimpleTable rows={pageRows} columns={pageColumns} emptyMessage="No page activity was returned by Matomo." />
                        </CardContent>
                    </Card>
                </TabPanel>

                <TabPanel value={tabIndex} index={2}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(0, 0.95fr)' }, gap: 2 }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Countries" subheader="Top countries by visit volume." />
                                <CardContent>
                                    {countryRows.length ? (
                                        <ReactApexChart
                                            type="bar"
                                            height={340}
                                            options={horizontalBarOptions(countryRows.map((row) => row.label))}
                                            series={[{ name: 'Visits', data: countryRows.map((row) => row.visits) }]}
                                        />
                                    ) : (
                                        <EmptyState message="No country breakdown was returned." />
                                    )}
                                </CardContent>
                            </Card>

                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Device Types" subheader="How visitors reached the site by device." />
                                <CardContent>
                                    {deviceRows.length ? (
                                        <ReactApexChart
                                            type="donut"
                                            height={340}
                                            options={donutOptions(deviceRows.map((row) => row.label))}
                                            series={deviceRows.map((row) => row.visits)}
                                        />
                                    ) : (
                                        <EmptyState message="No device rows were returned." />
                                    )}
                                </CardContent>
                            </Card>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2 }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Country Detail" subheader="Visits, visitors, and actions by country." />
                                <CardContent>
                                    <SimpleTable rows={countryRows} columns={geoColumns} emptyMessage="No country detail is available." />
                                </CardContent>
                            </Card>

                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardHeader title="Browsers" subheader="Browsers used by visitors during the selected range." />
                                <CardContent>
                                    <SimpleTable rows={browserRows} columns={trafficColumns} emptyMessage="No browser rows were returned." />
                                </CardContent>
                            </Card>
                        </Box>
                    </Stack>
                </TabPanel>
                </> : !loading ? (
                    <Card sx={getReportSurfaceSx(theme)}>
                        <CardContent>
                            <EmptyState message={hasMatomoConfigured ? 'No Matomo report data is available yet. Try loading the report again.' : 'Connect Matomo and select a site to populate this report.'} />
                        </CardContent>
                    </Card>
                ) : null}
            </Box>
        </Box></ThemeProvider>
    );
}
